import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  Cpu,
  Loader2,
  Power,
  RefreshCw,
  Settings2,
  Users,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { apiRequest } from "../components/lib/queryClient";
import { ApiResponse } from "../components/lib/utils";
import { useToast } from "../hooks/useToast";

export interface ServicePreset {
  key: string;
  type: string;
  default: boolean;
  description: string;
}

export interface SystemServiceStatus {
  enabled: boolean;
  running: boolean;
  description: string;
}

export interface SystemConfigStatus {
  services: Record<string, SystemServiceStatus>;
  master_strategies: number;
  total_subscriptions: number;
  service_manager_running: boolean;
}

export interface SystemConfigEntry {
  _id: string;
  key: string;
  value: any;
  description?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

const prettyLabel = (serviceKey: string) =>
  serviceKey
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export default function SystemConfig() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: status, isLoading: statusLoading } = useQuery<SystemConfigStatus>({
    queryKey: ["api/system-config/service-status"],
    queryFn: async () => {
      const res = await apiRequest<ApiResponse<SystemConfigStatus>>(
        "GET",
        "api/system-config/service-status",
        undefined,
        undefined,
        "crypto"
      );
      return res.data;
    },
    refetchInterval: 30000,
    retry: 1,
  });

  const { data: presets } = useQuery<ServicePreset[]>({
    queryKey: ["api/system-config/presets"],
    queryFn: async () => {
      const res = await apiRequest<ApiResponse<ServicePreset[]>>(
        "GET",
        "api/system-config/presets",
        undefined,
        undefined,
        "crypto"
      );
      return res.data ?? [];
    },
  });

  const { data: configs, isLoading: configsLoading } = useQuery<SystemConfigEntry[]>({
    queryKey: ["api/system-config"],
    queryFn: async () => {
      const res = await apiRequest<ApiResponse<SystemConfigEntry[]>>(
        "GET",
        "api/system-config",
        undefined,
        undefined,
        "crypto"
      );
      return res.data ?? [];
    },
  });

  const presetByKey = useMemo(() => {
    const map: Record<string, ServicePreset> = {};
    presets?.forEach((preset) => {
      map[preset.key] = preset;
    });
    return map;
  }, [presets]);

  const setConfig = useMutation({
    mutationFn: async ({ key, value, description }: { key: string; value: any; description?: string }) =>
      apiRequest(
        "PUT",
        "api/system-config",
        { key, value, description },
        undefined,
        "crypto"
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api/system-config"] });
      toast({ title: "Success", description: "Service configuration updated" });
    },
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: ["api/system-config"] });
      toast({
        title: "Failed to update configuration",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  const serviceEntries = Object.entries(status?.services ?? {});

  return (
    <div className="space-y-6">
      {/* Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Power className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Service manager</p>
              <p className="text-lg font-semibold">
                {statusLoading ? "..." : status?.service_manager_running ? "Running" : "Stopped"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active master strategies</p>
              <p className="text-lg font-semibold">{status?.master_strategies ?? 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Copy trading subscriptions</p>
              <p className="text-lg font-semibold">{status?.total_subscriptions ?? 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Service toggles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5" /> Background Services
          </CardTitle>
          <CardDescription>
            Enable or disable a crypto backend service. Changes are applied immediately by the
            backend service manager.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {statusLoading && (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading service status...
            </p>
          )}

          {!statusLoading && serviceEntries.length === 0 && (
            <p className="text-sm text-muted-foreground">No services reported by the backend.</p>
          )}

          {serviceEntries.map(([name, service]) => {
            const configKey = `services.${name}.enabled`;
            const description =
              service.description || presetByKey[configKey]?.description || "";
            return (
              <div
                key={name}
                className="flex items-center justify-between gap-4 border rounded-lg px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900">{prettyLabel(name)}</p>
                    <Badge
                      variant="outline"
                      className={
                        service.running
                          ? "text-green-700 border-green-300"
                          : "text-gray-500 border-gray-300"
                      }
                    >
                      <Activity className="w-3 h-3 mr-1" />
                      {service.running ? "Running" : "Stopped"}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500 truncate">{description}</p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-gray-500">{service.enabled ? "Enabled" : "Disabled"}</span>
                  <Switch
                    checked={!!service.enabled}
                    disabled={setConfig.isPending}
                    onCheckedChange={(checked) =>
                      setConfig.mutate({
                        key: configKey,
                        value: checked,
                        description: description || undefined,
                      })
                    }
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Stored configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" /> Stored Configuration
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => queryClient.invalidateQueries({ queryKey: ["api/system-config"] })}
            >
              Refresh
            </Button>
          </CardTitle>
          <CardDescription>
            Every key persisted in the <code>SystemConfigs</code> collection.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {configsLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : !configs?.length ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> No configuration stored yet — defaults are in
              use.
            </p>
          ) : (
            <div className="overflow-x-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Key</th>
                    <th className="px-3 py-2 font-medium">Value</th>
                    <th className="px-3 py-2 font-medium">Description</th>
                    <th className="px-3 py-2 font-medium">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map((config) => (
                    <tr key={config._id} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{config.key}</td>
                      <td className="px-3 py-2">
                        <Badge variant={config.value ? "default" : "secondary"}>
                          {String(config.value)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {config.description || presetByKey[config.key]?.description || "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {config.updatedAt ? new Date(config.updatedAt).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
